import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalPersistStorage } from '@/stores/persistStorage';

export interface Subscription {
  tier: 'free' | 'pro';
  expiresAt: Date | null;
  willRenew: boolean;
  provider: 'apple' | 'stripe' | null;
  productId: string | null;
  managementUrl: string | null;
  billingIssue: boolean;
}

const DEFAULT_SUBSCRIPTION: Subscription = {
  tier: 'free',
  expiresAt: null,
  willRenew: false,
  provider: null,
  productId: null,
  managementUrl: null,
  billingIssue: false,
};

function deriveIsPro(sub: Subscription): boolean {
  return sub.tier === 'pro' && sub.expiresAt != null && sub.expiresAt.getTime() > Date.now();
}

export interface SubscriptionState {
  subscription: Subscription;
  isPro: boolean;
  setSubscription: (sub: Subscription) => void;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set) => ({
      subscription: DEFAULT_SUBSCRIPTION,
      isPro: import.meta.env.DEV,
      setSubscription: (subscription) => set({ subscription, isPro: deriveIsPro(subscription) }),
    }),
    {
      name: 'brainday:subscription',
      storage: conditionalPersistStorage<SubscriptionState>(),
      partialize: (state) =>
        ({ subscription: state.subscription, isPro: state.isPro }) as SubscriptionState,
      // Never trust a persisted isPro on rehydrate — an expired subscription (or
      // a locally tampered flag) would otherwise keep Pro gates unlocked until a
      // Firestore/RevenueCat sync happens to overwrite it. Re-derive from
      // expiresAt every load. DEV keeps its always-Pro convenience.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<SubscriptionState>;
        const subscription = p.subscription ?? current.subscription;
        return {
          ...current,
          ...p,
          subscription,
          isPro: import.meta.env.DEV || deriveIsPro(subscription),
        };
      },
    },
  ),
);

export function selectCanCreateRoutine(routineCount: number, isPro: boolean): boolean {
  return isPro || routineCount < 2;
}

export function selectCanCreateHabit(habitCount: number, isPro: boolean): boolean {
  return isPro || habitCount < 3;
}
